import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Hint — the ONE explainer primitive (`hnt` family).
 *
 * The repo had no shared tooltip: every surface either used a bare `title`
 * attribute (invisible on touch, unstyled, 1–2s delayed) or rolled its own
 * inside a chart. Jargon then went unexplained everywhere else — "triage",
 * "proposal", "origin", "emergent" all shipped as words the user was
 * expected to already know.
 *
 * This is the fix, and it is deliberately dumb: a trigger the user can
 * HOVER (fine pointers) or TAP (coarse pointers — where hover does not
 * exist and `title` never appears), and a plain-language card that says
 * what the thing is and what happens if you act on it.
 *
 * Positioning is `fixed` off the trigger's measured rect, so a hint never
 * gets clipped by an `overflow: hidden` row or buried by a sibling's
 * stacking context. It closes on Escape, on an outside press, and on
 * scroll (a hint anchored to a moved element is a lie).
 *
 * Presentational + MUI-free. Custom properties inherit through the DOM, so
 * the card is rendered in place by default and keeps its host's tokens.
 * `portal` opts into document.body for triggers inside a transformed layer
 * (a pan/zoom canvas), where in-place `fixed` positioning breaks.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';
const CARD_W = 264;
const GAP = 8;
/** Hover is only an opener where hover actually exists. */
const canHover = () => typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
export function Hint({ text, label, children, className, ariaLabel, portal = false }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const wrapRef = useRef(null);
    const triggerRef = useRef(null);
    const cardRef = useRef(null);
    const id = useId();
    const place = useCallback(() => {
        const el = triggerRef.current;
        if (!el)
            return;
        const r = el.getBoundingClientRect();
        const vw = window.innerWidth;
        const width = Math.min(CARD_W, vw - 16);
        const left = Math.max(8, Math.min(r.left + r.width / 2 - width / 2, vw - width - 8));
        // Above by default; the real flip happens once the card's height is
        // known (a four-definition card is much taller than a one-liner, and
        // guessing from the trigger's position alone clipped it off-screen).
        setPos({ top: r.top - GAP, left, below: false });
    }, []);
    // Measure, then flip below if the card would run off the top. One extra
    // frame before paint — invisible to the reader, and it never clips.
    useLayoutEffect(() => {
        if (!open || !pos || pos.below)
            return;
        const card = cardRef.current;
        const trigger = triggerRef.current;
        if (!card || !trigger)
            return;
        const h = card.getBoundingClientRect().height;
        if (pos.top - h < 8) {
            setPos({ top: trigger.getBoundingClientRect().bottom + GAP, left: pos.left, below: true });
        }
    }, [open, pos]);
    const show = useCallback(() => { place(); setOpen(true); }, [place]);
    const hide = useCallback(() => setOpen(false), []);
    useEffect(() => {
        if (!open)
            return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                hide();
            }
        };
        const onDown = (e) => {
            const t = e.target;
            if (!wrapRef.current?.contains(t) && !cardRef.current?.contains(t))
                hide();
        };
        // A hint pinned to an element that scrolled away points at nothing.
        window.addEventListener('keydown', onKey, true);
        window.addEventListener('pointerdown', onDown, true);
        window.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
        return () => {
            window.removeEventListener('keydown', onKey, true);
            window.removeEventListener('pointerdown', onDown, true);
            window.removeEventListener('scroll', hide, true);
            window.removeEventListener('resize', hide);
        };
    }, [open, hide]);
    return (_jsxs("span", { className: "hnt", ref: wrapRef, children: [_jsx("button", { type: "button", ref: triggerRef, className: `hnt-trigger ${className || ''}`, "aria-label": ariaLabel || (typeof label === 'string' ? `What is ${label}?` : 'What is this?'), "aria-expanded": open, "aria-describedby": open ? id : undefined, onClick: (e) => { e.stopPropagation(); if (open)
                    hide();
                else
                    show(); }, onPointerEnter: () => { if (canHover())
                    show(); }, onPointerLeave: () => { if (canHover())
                    hide(); }, onFocus: () => show(), onBlur: (e) => {
                    const t = e.relatedTarget;
                    if (!wrapRef.current?.contains(t) && !cardRef.current?.contains(t))
                        hide();
                }, children: children || _jsx(HelpCircle, { size: 12, "aria-hidden": "true" }) }), open && pos && (() => {
                const card = (_jsxs("span", { id: id, ref: cardRef, role: "tooltip", className: `hnt-card ${pos.below ? 'hnt-card-below' : ''}`, style: {
                        top: pos.top,
                        left: pos.left,
                        width: Math.min(CARD_W, (typeof window !== 'undefined' ? window.innerWidth : CARD_W) - 16),
                    }, children: [label && _jsx("strong", { className: "hnt-label", children: label }), _jsx("span", { className: "hnt-text", children: text })] }));
                if (portal && typeof document !== 'undefined') {
                    // Wrapped in `.hnt` so the card resolves the family's tokens
                    // outside its trigger's subtree; `.hnt-portal` is display: contents
                    // so the wrapper itself never paints a box at the end of <body>.
                    return createPortal(_jsx("span", { className: "hnt hnt-portal", children: card }), document.body);
                }
                return card;
            })()] }));
}
export default Hint;
